/* eslint-disable @typescript-eslint/no-explicit-any */
import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate, useSearchParams } from "react-router-dom";

import { ProfileTab } from "enums/ProfileTabs";

import { useConsolidatedWorkerByCWID } from "services/input-core/consolidated-workers/get-consolidated-worker-by-cwid";
import { useWorkItemsFromCWorker } from "services/input-core/work-items/get-work-items-from-cworker";
import { useWorkersByCWEntityID } from "services/input-core/workers/get-all-workers-by-cw-entity-id";
import ButtonTS from "components/Forms/CustomButtons/ButtonTS";

import { ArrowForwardIos } from "@material-ui/icons";

import GridContainer from "components/UI/Grid/GridContainer";
import GridItemTS from "components/UI/Grid/GridItemTS";
import { TabContent, TabSystem } from "components/UI/TabSystem";
import Header from "./components/Header";
import { WorkerRelatedTable } from "components/Tables/WorkerRelatedTable";

import GeneralTab, { BuildDetailsSection } from "./tabs/GeneralTab/GeneralTab";
import ActivityItemsTab from "./tabs/ActivityItemsTab/ActivityItemsTab";

import { tryRetrieveDateFromUrlParams } from "helpers/DateHelper";
import { useActivityItemsFromWorkerWithStack } from "services/input-core/activity-items/get-activity-items-from-worker-with-stacks";
import { useActivityItemContext } from "store/activityItem";
import { useConsolidatedWorkerContext } from "store/consolidatedWorker";

const ConsolidatedWorkerProfile: React.FC = () => {
  const navigate = useNavigate();
  const { t } = useTranslation("worker-profile");
  const { getConsolidatedProfile } = useConsolidatedWorkerContext();

  const [searchParams] = useSearchParams();
  const workerId = searchParams.get("id");

  const { data, isLoading } = useConsolidatedWorkerByCWID(workerId, {
    enabled: !!workerId,
    onSuccess: (response: any) => {
      getConsolidatedProfile(response);
    },
  });
  
  const [date, setDate] = useState<Date>(() => tryRetrieveDateFromUrlParams());

  const {
    setActivityItems,
    setLoading: setLoadingActivity,
    setRefetching: setRefetchingActivity,
  } = useActivityItemContext();

  const {
    data: activityItemsQuery,
    isLoading: isActivityItemsLoadingQuery,
    isRefetching: isActivityItemsRefetchingQuery,
    isInitialLoading: isActivityItemsInitialLoading,
    refetch: refetchActivityItems,
  } = useActivityItemsFromWorkerWithStack(data?.consolidatedWorkerID, date, {
    enabled: !isLoading && !!data,
  });

  const {
    data: workItems,
    isLoading: isWorkItemsLoading,
    refetch: refetchWorkItems,
  } = useWorkItemsFromCWorker(data?.consolidatedWorkerID, date, {
    enabled: !isLoading && !!data?.consolidatedWorkerID,
  });

  const {
    data: workersFromConsolidatedWorker,
    isLoading: isLoadingWorkersFromConsolidatedWorker,
  } = useWorkersByCWEntityID(data?._id, true, {
    enabled: !isLoading && !!data?._id,
  });

  useEffect(() => {
    setLoadingActivity(isActivityItemsLoadingQuery);
    setRefetchingActivity(isActivityItemsRefetchingQuery);
    if (activityItemsQuery) setActivityItems(activityItemsQuery);
  }, [
    activityItemsQuery,
    isActivityItemsLoadingQuery,
    isActivityItemsRefetchingQuery,
    setActivityItems,
    setLoadingActivity,
    setRefetchingActivity,
  ]);

  const tabs = [
    {
      id: ProfileTab.GENERAL,
      title: t("General", { ns: "profile" }),
    },
    {
      id: ProfileTab.ACTIVITY_ITEMS,
      title: t("Activity Items", { ns: "profile" }),
    },
    {
      id: ProfileTab.WORKERS_RELATED,
      title: t("Workers Related", { ns: "profile" }),
    },
  ];

  const workerBuildDetails: BuildDetailsSection[] = [
    {
      title: t("ConsolidatedWorker Details", { ns: "profile" }),
      items: [
        {
          text: t("Consolidated Worker ID (Fieldglass)", { ns: "profile" }),
          value: data?.consolidatedWorkerFieldglassID,
        },
        {
          text: t("ConsolidatedWorker Name", { ns: "profile" }),
          value: `${data?.fullName}`,
        },
        {
          text: t("Supplier", { ns: "profile" }),
          value: data?.vendorName,
        },
      ],
    },
    {
      title: t("Secondary Details", { ns: "profile" }),
      items: [
        {
          text: t("Belongs to Supplier", { ns: "profile" }),
          value: `${data?.vendorNumber} ${data?.vendorName}`,
        },
      ],
    },
  ];

  const workersFormatted = (data: any) =>
    data?.map(
      (worker: { workerID: string; firstName: string; lastName: string }) => ({
        code: worker.workerID,
        name: `${worker.firstName + " "}${worker.lastName}`,
        email: "",
        actions: (
          <div className="actions-right">
            <ButtonTS
              justIcon
              round
              color="info"
              size="sm"
              onClick={() =>
                navigate(
                  `/app/workers/profile?profile=worker"&id=${worker.workerID}`
                )
              }
            >
              <ArrowForwardIos />
            </ButtonTS>
          </div>
        ),
      })
    ) || [];

  return (
    <GridContainer>
      <GridItemTS xs={12}>
        <Header
          data={{
            fieldglassId: data?.consolidatedWorkerID,
            name: data?.fullName,
            status: "",
            zIdentifier: "",
            startDate: data?.startDate,
            endDate: data?.endDate,
          }}
          isLoading={isLoading}
          type="worker"
        />
      </GridItemTS>

      <GridItemTS xs={12} style={{ marginTop: "1rem" }}>
        <TabSystem tabs={tabs} syncWithUrlParams>
          <TabContent value={ProfileTab.GENERAL}>
            <GeneralTab
              isLoading={isLoading}
              buildDetails={workerBuildDetails}
            />
          </TabContent>

          <TabContent value={ProfileTab.ACTIVITY_ITEMS}>
            <ActivityItemsTab
              fieldglassId={data?.consolidatedWorkerFieldglassID}
              entityDate={{ start: data?.startDate, end: data?.endDate }}
              date={date}
              setDate={setDate}
              isActivityItemsInitialLoading={isActivityItemsInitialLoading}
              refetchActivityItems={refetchActivityItems}
              workItems={workItems}
              isWorkItemsLoading={isWorkItemsLoading}
              refetchWorkItems={refetchWorkItems}
              profile={"WORKER"}
              supplierCode={data?.vendorNumber}
            />
          </TabContent>

          <TabContent value={ProfileTab.WORKERS_RELATED}>
            <WorkerRelatedTable
              data={
                workersFromConsolidatedWorker
                  ? workersFormatted(workersFromConsolidatedWorker)
                  : []
              }
              isLoading={isLoadingWorkersFromConsolidatedWorker}
              omit={["email", "vendorName", "vendorCode", "numberDNI"]}
              initialRowsPerPage={5}
            />
          </TabContent>
        </TabSystem>
      </GridItemTS>
    </GridContainer>
  );
};

export default ConsolidatedWorkerProfile;
